import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useFormik } from 'formik';
import style from './ExemploBaseFilter.module.scss';
import { useParams } from 'react-router-dom';
import { Pagination } from '../../../utils/Pagination';
import { Button } from 'primereact/button';
import BaseConfigFilter from './BaseConfigFilter';

export function FilterInputs({
    formik,
    formRef,
    initialValues,
    labelPesquisar = 'Pesquisar',
    iconPesquisar = 'pi pi-search',
    noEmptyInputsOnClear = false,
    ...rest
}) {
    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    flex: '1',
                    alignSelf: 'initial',
                }}
            >
                <div className={`${style.buttonSemRotulo}`}>
                    <Button
                        label="Limpar"
                        className="p-button-secondary"
                        type="button"
                        onClick={() => {
                            const clearInputs = noEmptyInputsOnClear
                                ? Object.keys(initialValues)
                                      .map((key) => ({ [key]: '' }))
                                      .reduce((prev, curr) => ({ ...prev, ...curr }), {})
                                : initialValues;
                            formik.setValues(clearInputs);
                            if (formRef.current) {
                                formRef.current[0].focus();
                            }
                        }}
                        icon="pi pi-filter-slash"
                    />
                </div>
                <div className={`${style.buttonSemRotulo}`}>
                    <Button label={labelPesquisar} icon={iconPesquisar} type="submit" {...rest} />
                </div>
            </div>
        </>
    );
}

function BaseFilter(
    {
        onFiltro,
        className = '',
        labelPesquisar = 'Pesquisar',
        iconPesquisar = 'pi pi-search',
        buscarOnLoad = true,
        actionOnCreate = (formik) => formik,
        onLocalFilter,
        children,
        showFilterButtons = true,
        actionOnSearch,
        initialValues,
        customFilter,
        prepareInitialValues,
        noEmptyInputsOnClear = false,
    },
    ref
) {
    const params = useParams();
    const formRef = useRef(null);
    const [filtroConfig, setFiltroConfig] = useState(null);
    const localFilter = (filtros) => {
        const pagination = new Pagination();
        pagination.filters = filtros;
        onLocalFilter({ filtros, pagination, params });
    };

    const formik = useFormik({
        initialValues: {},
        onSubmit: (data) => {
            const search = filtroConfig.onSubmit(
                { ...data, pageParams: params },
                onFiltro ?? localFilter,
                customFilter
            );
            if (actionOnSearch) {
                actionOnSearch(search);
            }
        },
    });

    useEffect(() => {
        const init = async () => {
            const filter = new BaseConfigFilter(initialValues);
            if (prepareInitialValues) {
                const value = await prepareInitialValues(filter.initialValues);
                filter.setInitialValues(value);
            }
            formik.setValues(filter.initialValues);
            setFiltroConfig(filter);
        };

        init();
    }, []);

    useImperativeHandle(
        ref,
        () => {
            return { formik };
        },
        [formik]
    );

    useEffect(() => {
        actionOnCreate(formik);
    }, []);

    useEffect(() => {
        if (filtroConfig && buscarOnLoad) formik.submitForm();
    }, [filtroConfig]);

    function renderButtons(formRef) {
        if (!showFilterButtons) return <></>;

        return (
            <>
                <div className={`${style.buttonSemRotulo}`}>
                    <Button
                        label="Limpar"
                        className="p-button-secondary"
                        type="button"
                        onClick={() => {
                            const clearInputs = !noEmptyInputsOnClear
                                ? Object.keys(filtroConfig.initialValues)
                                      .map((key) => ({ [key]: '' }))
                                      .reduce((prev, curr) => ({ ...prev, ...curr }), {})
                                : filtroConfig.initialValues;
                            formik.setValues(clearInputs);
                            if (formRef.current) {
                                formRef.current[0].focus();
                            }
                        }}
                        icon="pi pi-filter-slash"
                    />
                </div>
                <div className={`${style.buttonSemRotulo}`}>
                    <Button label={labelPesquisar} icon={iconPesquisar} type="submit" />
                </div>
            </>
        );
    }

    return (
        <form
            ref={formRef}
            onSubmit={formik.handleSubmit}
            className={`p-col-12 p-grid p-fluid ${className}`}
        >
            {filtroConfig && React.cloneElement(children, { ...children?.props, formik })}
            {showFilterButtons && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        flex: '1',
                        alignSelf: 'initial',
                    }}
                >
                    {filtroConfig && renderButtons(formRef)}
                </div>
            )}
        </form>
    );
}

export default forwardRef(BaseFilter);
